<!-- eslint-disable @angular-eslint/template/no-inline-styles -->
<!-- Hidden file input that we trigger programmatically -->
<input
  #fileInput
  type="file"
  class="hidden"
  [multiple]="useCropper() ? false : !!maxFiles() && maxFiles() > 1"
  [accept]="accept()"
  (change)="onFileSelected($event)"
  [disabled]="isDisabled"
  [id]="id()"
/>

<!-- Main container -->
<div class="custom-file-input-container w-full {{ label() ? 'mt-[25px]' : '' }}">
  <div
    class="flex justify-center"
    [ngClass]="{
      'flex-wrap': wrapLabel(),
    }"
  >
    <!-- Floating -->
    @if (label()) {
      <label
        [for]="id()"
        class="text-[14px] font-bold z-10 transition-all duration-200 ease-in-out pointer-events-none max-w-full"
        [ngClass]="{
          '-top-6': isDisabled || managedFiles.length > 0,
          '-top-2.5': !isDisabled && managedFiles.length === 0,
          'text-red-500': !isDisabled && isInvalid && displayError(),
          'text-blue-500': !isDisabled && !isInvalid && isDraggingOver,
          'text-gray-700': !isDisabled && !isInvalid && !isDraggingOver,
          'text-gray-500': isDisabled,
          'whitespace-nowrap overflow-hidden text-ellipsis block': !wrapLabel(),
          'whitespace-normal break-words block': wrapLabel(),
        }"
        [title]="label()"
        >{{ label() }}</label
      >
    }

    <!-- Drop Zone Area -->
    <div
      class="drop-zone-area transition-colors duration-200 w-full"
      [ngClass]="{
        'border-error': !isDisabled && isInvalid && displayError(),
        'border-transparent': !isDisabled && ngControl?.valid && ngControl?.touched,
        'border-blue-500 ring-1 ring-blue-500 bg-blue-50':
          !isDisabled && !isInvalid && isDraggingOver,
        'border-gray-300':
          !isDisabled &&
          !(isInvalid && displayError()) &&
          !isDraggingOver &&
          !(ngControl?.valid && ngControl?.touched),
        'bg-gray-100 text-gray-400 border-gray-200': isDisabled,
        'cursor-not-allowed': isDisabled,
        'cursor-pointer': !isDisabled && managedFiles.length === 0,
      }"
      (click)="!managedFiles.length && !isDisabled && triggerFileInputClick()"
      (dragover)="onDragOver($event)"
      (dragleave)="onDragLeave($event)"
      (drop)="onDrop($event)"
      (blur)="onTouched()"
      [attr.tabindex]="isDisabled ? -1 : 0"
    >
      @if (managedFiles.length > 0) {
        <!-- Grid for displaying selected files -->
        <div class="flex flex-wrap gap-4">
          @for (file of managedFiles; track $index) {
            <div
              [class]="displayType() === 'circle' || maxFiles() > 1 ? 'w-32' : 'w-full'"
              class="flex flex-col items-center"
            >
              <!-- Preview container -->
              <div
                [class.rounded-xl]="displayType() === 'square'"
                [class]="displayType() === 'circle' ? 'w-32' : 'w-full'"
                class="relative overflow-hidden flex items-center justify-center"
                [class.bg-primary_light]="!file.previewUrl || file.fileInfo.previewType !== 'img'"
                [class.bg-white]="file.previewUrl && file.fileInfo.previewType === 'img'"
                [ngClass]="displayType() === 'circle' ? 'h-32' : height() || 'h-32'"
                [class.rounded-full]="
                  file.fileInfo.previewType === 'img' || displayType() === 'circle'
                "
              >
                <!-- Upload Progress / Status -->
                @if (getUploadStatus(file); as status) {
                  <div class="upload-overlay">
                    @if (status === 'in-progress' && getUploadProgress(file); as progress) {
                      <div
                        class="progress-circle"
                        [style.background]="
                          'conic-gradient(#4caf50 ' + progress + '%, #e0e0e0 ' + progress + '%)'
                        "
                      >
                        <span class="progress-text">{{ progress }}%</span>
                      </div>
                    }

                    @if (status === 'success') {
                      <div class="status-icon success">
                        <i class="fa-solid fa-check"></i>
                      </div>
                    }

                    @if (status === 'error') {
                      <div class="status-icon error">
                        <i class="fa-solid fa-times"></i>
                      </div>
                    }
                  </div>
                }

                <!-- Image preview -->
                @if (file.previewUrl && file.fileInfo.previewType === 'img') {
                  <img
                    [src]="file.previewUrl"
                    [alt]="file.fileInfo.name"
                    class="w-full h-full object-cover"
                  />
                }

                <!-- Icon preview -->
                @if (!file.previewUrl) {
                  <div class="text-primary text-[32px]">
                    <i [class]="getIconForFile(file)"></i>
                  </div>
                }
              </div>

              <!-- File info (ALWAYS visible) -->
              <div class="mt-2 w-full text-center px-1">
                @if (file.fileInfo.previewType !== 'img') {
                  <p
                    class="text-[12px] font-semibold text-basic break-all line-clamp-2"
                    [title]="file.fileInfo.name"
                  >
                    {{ file.fileInfo.name }}
                  </p>
                }

                <!-- Actions -->
                <div class="mt-1 flex justify-center gap-4">
                  @if (!file.isNew && file.url && isPreviewable(file)) {
                    <button
                      type="button"
                      (click)="previewFile(file, $event)"
                      class="cursor-pointer"
                      aria-label="Preview file"
                    >
                      <i class="fa-solid fa-eye text-[14px]"></i>
                    </button>
                  }

                  @if (!file.isNew && file.url) {
                    <button
                      type="button"
                      (click)="downloadFile(file, $event)"
                      class="cursor-pointer"
                      aria-label="Download file"
                    >
                      <i class="fa-solid fa-download text-[14px]"></i>
                    </button>
                  }

                  @if (!isDisabled) {
                    <button
                      type="button"
                      (click)="removeFile($index, $event)"
                      class="text-error cursor-pointer"
                      aria-label="Remove file"
                    >
                      <i class="fa-solid fa-xmark text-[14px]"></i>
                    </button>
                  }
                </div>
              </div>
            </div>
          }

          <!-- "Add More" button -->
          @if (
            !!maxFiles() && maxFiles() > 1 && !isDisabled && maxFiles() !== managedFiles.length
          ) {
            <button
              type="button"
              (click)="triggerFileInputClick()"
              class="add-more-button w-32 bg-gray-50 rounded-md border-2 border-dashed border-gray-300 flex flex-col items-center justify-center text-gray-500 hover:border-blue-500 hover:text-blue-500 transition-colors"
              [ngClass]="displayType() === 'circle' ? 'h-32' : height() || 'h-32'"
            >
              <i class="fa-solid fa-plus text-[24px]"></i>
              <span class="text-[12px] mt-1">{{
                'SHARED.INPUTS_CUSTOM-FILE-INPUT.ADD_MORE' | translate
              }}</span>
            </button>
          }
        </div>
      } @else {
        <!-- Placeholder for when no files are selected -->
        <div class="w-full h-full flex flex-col items-center justify-center text-center">
          <!-- Styled Icon Box (Circle/Square) -->
          <div
            [class.rounded-full]="displayType() === 'circle'"
            [class.rounded-xl]="displayType() === 'square'"
            [class]="displayType() === 'circle' ? 'w-32' : 'w-full'"
            class="bg-primary_light flex items-center justify-center"
            [ngClass]="displayType() === 'circle' ? 'h-32' : height() || 'h-32'"
          >
            <i [class]="getPlaceholderIcon()" class="text-[24px] text-primary"></i>
          </div>

          <p class="mt-2 font-bold text-caption text-[12px]">
            {{ 'SHARED.INPUTS_CUSTOM-FILE-INPUT.DRAG_AND_DROP_OR_CLICK' | translate }}
          </p>

          <p class="text-[12px] text-caption_light mt-1">
            {{
              (!!maxFiles() && maxFiles() > 1
                ? 'SHARED.INPUTS_CUSTOM-FILE-INPUT.MULTIPLE_FILES_HINT'
                : 'SHARED.INPUTS_CUSTOM-FILE-INPUT.SINGLE_FILE_HINT'
              ) | translate
            }}
          </p>
        </div>
      }
    </div>
  </div>

  <!-- Validator info -->
  @if (maxFiles() > 1) {
    <div class="mt-2 text-[14px] text-gray-600">
      <span>{{ 'SHARED.INPUTS_CUSTOM-FILE-INPUT.FILES_LABEL' | translate }}:</span>
      <span class="font-medium">{{ managedFiles.length }}</span>
      <span class="ml-2 text-gray-500"
        >({{ 'SHARED.INPUTS_CUSTOM-FILE-INPUT.MINIMUM_LABEL' | translate }} {{ minFiles() }})</span
      >
    </div>
  }

  <!-- Error display -->
  @if (displayError()) {
    <lib-custom-error-display [errors]="errors()" [control]="ngControl"></lib-custom-error-display>
  }
</div>

<!-- Único modal reutilizable para vista previa de archivos -->
<lib-popup
  [id]="previewModalId"
  [title]="currentPreviewFile?.fileInfo?.name || ''"
  (confirm)="closePreview()"
>
  @if (currentPreviewFile?.url) {
    @if (currentPreviewFile && isImage(currentPreviewFile)) {
      <div class="flex justify-center">
        <img
          [src]="getSafeUrl(currentPreviewFile.url)"
          [alt]="currentPreviewFile.fileInfo.name || ''"
          class="max-w-full max-h-[80vh]"
        />
      </div>
    }
    @if (currentPreviewFile && isPdf(currentPreviewFile)) {
      <iframe
        [src]="getSafeUrl(currentPreviewFile.url)"
        width="100%"
        height="600px"
        frameborder="0"
      ></iframe>
    }
    @if (currentPreviewFile && isVideo(currentPreviewFile)) {
      <video controls class="max-w-full max-h-[80vh]">
        <source [src]="getSafeUrl(currentPreviewFile.url)" type="video/mp4" />
        Your browser does not support the video tag.
      </video>
    }
    @if (currentPreviewFile && isAudio(currentPreviewFile)) {
      <audio controls class="w-full">
        <source [src]="getSafeUrl(currentPreviewFile.url)" type="audio/mpeg" />
        Your browser does not support the audio element.
      </audio>
    }
  }
</lib-popup>

<!-- Cropper -->
@if (useCropper() && cropperFile) {
  <lib-custom-cropper
    [modalId]="cropperModalId"
    [file]="cropperFile"
    [aspectRatio]="aspectRatio()"
    (croppedFile)="onCroppedFile($event)"
    (canceled)="onCropCanceled()"
  ></lib-custom-cropper>
}
